import { useEffect, useState } from 'react';

// Eases out the count so the number decelerates as it approaches the target.
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const useAnimatedNumber = (end, { duration = 1500, decimals = 0, delay = 0 } = {}) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (end === null || end === undefined || Number.isNaN(end)) {
      setValue(0);
      return undefined;
    }

    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      setValue(end);
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setValue(end);
      return undefined;
    }

    let rafId;
    let timeoutId;
    let startTimestamp = null;

    const factor = Math.pow(10, decimals);

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const eased = easeOutCubic(progress);
      const next = Math.round(eased * end * factor) / factor;
      setValue(next);
      if (progress < 1) {
        rafId = window.requestAnimationFrame(step);
      }
    };

    timeoutId = window.setTimeout(() => {
      rafId = window.requestAnimationFrame(step);
    }, delay);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [end, duration, decimals, delay]);

  return value;
};

export default useAnimatedNumber;
